/**
 * Acquisition attribution utilities
 *
 * Captures WHERE a visitor came from (UTM tags or referring site) on first
 * touch and preserves it through signup so every new player can be attributed
 * to a source. Complements the SRC-/AFF- source-code system (referralUtils.js
 * + source_code_players): this covers organic search, social, and untagged
 * ad traffic that never carries an explicit ?src code.
 *
 * Storage mirrors referralUtils: cookie is primary (more reliable on mobile /
 * private-mode browsers) with localStorage as an expiry-tracked backup. The
 * attribution window here is 30 DAYS (vs the referral code's 24h) because an
 * acquisition source should still be credited weeks after the first click.
 *
 * Every path is wrapped in try/catch and never throws: localStorage can be
 * blocked and document.cookie can be unavailable, and a tracking helper must
 * never break app boot or signup.
 */

import logger from "./logger";

const ACQUISITION_STORAGE_KEY = "cluck_acquisition";
const ACQUISITION_COOKIE_NAME = "cluck_acq";
const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

// Registrable-domain resolution needs to know the common multi-label public
// suffixes so "shop.example.co.uk" collapses to "example.co.uk" and not
// "co.uk". Not exhaustive (that would need the full public-suffix list); it
// covers the country TLDs cluck.us actually sees referral traffic from.
const SECOND_LEVEL_SUFFIXES = new Set([
  "co.uk",
  "com.au",
  "co.nz",
  "co.za",
  "com.br",
  "co.in",
  "co.jp",
  "com.mx",
  "co.kr",
  "com.sg",
  "com.hk",
  "co.id",
  "com.tr",
  "co.il",
]);

// Known referrer hosts mapped to a canonical source label, keyed by
// registrable domain. google.* and bing.* span too many TLDs to enumerate, so
// they are matched by first label instead (see resolveReferrerLabel).
const KNOWN_DOMAIN_LABELS = {
  "facebook.com": "facebook",
  "instagram.com": "instagram",
  "t.co": "x",
  "x.com": "x",
  "twitter.com": "x",
  "duckduckgo.com": "duckduckgo",
  "reddit.com": "reddit",
  "youtube.com": "youtube",
  "tiktok.com": "tiktok",
};

// Auth + payment INFRASTRUCTURE hosts. When a visitor is bounced back from an
// OAuth provider (Google/Apple/Microsoft sign-in) or a payment gateway, they
// land on our return page (e.g. /auth/callback) with the PROVIDER as
// document.referrer — accounts.google.com, appleid.apple.com, pay.com, etc.
// Left unchecked that clobbers the true first-touch source: every Google
// sign-in became source "google", every card return became "pay.com" (this is
// exactly why depositor attribution read as google/pay.com noise). Treat these
// as NON-acquisition, like a self-referral.
//
// Matched on the FULL hostname (not the registrable domain), so real Google
// SEARCH traffic (www.google.com / google.com / google.co.uk) is unaffected —
// only the auth subdomain accounts.google.com is ignored.
const IGNORED_REFERRER_HOSTS = new Set([
  "accounts.google.com",
  "accounts.youtube.com",
  "appleid.apple.com",
  "login.microsoftonline.com",
  "login.microsoft.com",
  "login.live.com",
  "pay.com",
  "js.pay.com",
  "checkout.pay.com",
]);

// Any auth-flow subdomain (accounts./login./oauth./signin./auth.) is
// infrastructure, never an acquisition source.
const AUTH_SUBDOMAIN_RE = /^(accounts|login|oauth|signin|auth)\./;

function isIgnoredReferrerHost(host) {
  return IGNORED_REFERRER_HOSTS.has(host) || AUTH_SUBDOMAIN_RE.test(host);
}

// Internal RETURN landings (OAuth callback, gateway return). A page load here
// is never a real acquisition touch — it's the tail of a flow that started
// on-site — so we do not capture on these paths, a second guard against a
// provider referrer becoming the attributed source.
function isInternalReturnPath(path) {
  if (typeof path !== "string") return false;
  return (
    path === "/auth" ||
    path.startsWith("/auth/") ||
    path.startsWith("/checkout") ||
    path.startsWith("/payment") ||
    path.startsWith("/kyc/callback")
  );
}

// ============ COOKIE HELPERS ============

function setCookie(name, value, expiryMs) {
  try {
    const expires = new Date(Date.now() + expiryMs).toUTCString();
    // SameSite=Lax so the cookie survives cross-site navigation from an ad or
    // referring site into cluck.us.
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch (e) {
    logger.warn("Failed to set acquisition cookie:", e);
  }
}

function getCookie(name) {
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [cookieName, ...rest] = cookie.trim().split("=");
      if (cookieName === name) {
        return decodeURIComponent(rest.join("="));
      }
    }
    return null;
  } catch (e) {
    logger.warn("Failed to read acquisition cookie:", e);
    return null;
  }
}

function deleteCookie(name) {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  } catch (e) {
    logger.warn("Failed to delete acquisition cookie:", e);
  }
}

// ============ NORMALIZATION HELPERS ============

/**
 * Trim a value and return null when it is empty / missing. Keeps stored
 * payloads consistent (every field is a non-empty string or null).
 */
function cleanParam(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Reduce a hostname to its registrable domain using a small known list of
 * multi-label suffixes. "www." is assumed already stripped by the caller.
 */
function registrableDomain(host) {
  const labels = host.split(".").filter(Boolean);
  if (labels.length <= 2) return labels.join(".");
  const lastTwo = labels.slice(-2).join(".");
  if (SECOND_LEVEL_SUFFIXES.has(lastTwo)) {
    return labels.slice(-3).join(".");
  }
  return labels.slice(-2).join(".");
}

/**
 * True when a hostname belongs to cluck.us (self-referral). Uses an exact /
 * dotted-suffix check so "notcluck.us" is NOT treated as internal.
 */
function isSelfReferral(host) {
  return host === "cluck.us" || host.endsWith(".cluck.us");
}

/**
 * Resolve a normalized source label from a referrer hostname (already
 * stripped of a leading "www."). Returns a canonical label for known hosts,
 * otherwise the bare registrable domain.
 */
function resolveReferrerLabel(host) {
  const base = registrableDomain(host);
  const firstLabel = base.split(".")[0];

  if (firstLabel === "google") return "google";
  if (firstLabel === "bing") return "bing";
  if (KNOWN_DOMAIN_LABELS[base]) return KNOWN_DOMAIN_LABELS[base];

  return base;
}

/**
 * Parse document.referrer into { domain, url, label } or null when it is
 * absent or a self-referral (internal navigation). `domain` is the normalized
 * registrable domain; `label` is the mapped canonical source label.
 */
function parseReferrer(referrer) {
  const raw = cleanParam(referrer);
  if (!raw) return null;

  let hostname;
  try {
    hostname = new URL(raw).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!hostname) return null;

  const host = hostname.replace(/^www\./, "");
  if (isSelfReferral(host)) return null;
  // OAuth/payment infrastructure is not an acquisition source (see the set).
  if (isIgnoredReferrerHost(host)) return null;

  return {
    domain: registrableDomain(host),
    url: raw,
    label: resolveReferrerLabel(host),
  };
}

// ============ EXPIRY / STORAGE ============

function isExpired(firstSeenAt) {
  const t = Date.parse(firstSeenAt);
  if (Number.isNaN(t)) return false; // lenient: keep records with odd timestamps
  return Date.now() - t > EXPIRY_MS;
}

function clearAcquisition() {
  try {
    localStorage.removeItem(ACQUISITION_STORAGE_KEY);
  } catch (e) {
    logger.warn("Failed to clear acquisition from localStorage:", e);
  }
  deleteCookie(ACQUISITION_COOKIE_NAME);
}

function storeAcquisition(payload) {
  const serialized = JSON.stringify(payload);

  // Cookie first (primary, more reliable on mobile). Browser owns expiry.
  setCookie(ACQUISITION_COOKIE_NAME, serialized, EXPIRY_MS);

  // localStorage backup (expiry tracked against payload.first_seen_at).
  try {
    localStorage.setItem(ACQUISITION_STORAGE_KEY, serialized);
  } catch (e) {
    // localStorage may be blocked; cookie is primary anyway.
    logger.warn(
      "Failed to store acquisition in localStorage (cookie backup active):",
      e,
    );
  }
}

/**
 * Get the stored acquisition payload if still within the 30-day window.
 * Checks localStorage first (has explicit expiry), falls back to the cookie
 * (browser-managed expiry). Returns the parsed object or null.
 * @returns {object|null}
 */
export function getStoredAcquisition() {
  try {
    const stored = localStorage.getItem(ACQUISITION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && !isExpired(parsed.first_seen_at)) {
        return parsed;
      }
      // Expired: clear both stores and fall through to a null result.
      clearAcquisition();
      return null;
    }
  } catch (e) {
    logger.warn("Failed to read acquisition from localStorage:", e);
  }

  // Fallback to cookie (survives when localStorage is unavailable).
  try {
    const cookieValue = getCookie(ACQUISITION_COOKIE_NAME);
    if (cookieValue) {
      const parsed = JSON.parse(cookieValue);
      if (parsed && !isExpired(parsed.first_seen_at)) {
        return parsed;
      }
    }
  } catch (e) {
    logger.warn("Failed to read acquisition from cookie:", e);
  }

  return null;
}

/**
 * Read UTM params + document.referrer from the current page and persist the
 * acquisition source on FIRST touch.
 *
 * source_label resolution:
 *   1. utm_source present            -> utm_source, trimmed + lower-cased
 *   2. else external referrer present -> normalized referrer domain label
 *   3. else                          -> null (nothing to attribute)
 *
 * First-touch / priority rules against any already-stored acquisition:
 *   - stored HAS utm_source                       -> never overwrite (first utm wins)
 *   - stored has NO utm_source, utm present now   -> upgrade to the utm capture
 *   - stored has NO utm_source, only referrer now -> keep existing (first referrer wins)
 *
 * Unlike referralUtils (which strips ?ref), the UTM params are LEFT on the URL
 * so Google Analytics and any other client-side tag can still read them.
 *
 * Never throws: storage can be blocked and this runs on every app boot.
 */
export function captureAcquisitionFromURL() {
  if (typeof window === "undefined") return;

  try {
    // Never attribute from an internal return landing (OAuth callback / gateway
    // return). Their provider referrer would otherwise clobber the true source.
    if (isInternalReturnPath(window.location.pathname)) return;

    const params = new URLSearchParams(window.location.search);

    const utmSource = cleanParam(params.get("utm_source"));
    const utmMedium = cleanParam(params.get("utm_medium"));
    const utmCampaign = cleanParam(params.get("utm_campaign"));
    const utmContent = cleanParam(params.get("utm_content"));
    const utmTerm = cleanParam(params.get("utm_term"));

    const referrer = parseReferrer(
      typeof document !== "undefined" ? document.referrer : "",
    );

    // Resolve the canonical source label (utm beats referrer).
    let sourceLabel = null;
    if (utmSource) {
      sourceLabel = utmSource.toLowerCase();
    } else if (referrer) {
      sourceLabel = referrer.label;
    }

    // Nothing to attribute (no utm, no external referrer): do not create an
    // empty record, so a genuine source touch later can still be first-touch.
    if (!sourceLabel) return;

    const hasUtmNow = Boolean(utmSource);
    const existing = getStoredAcquisition();

    if (existing) {
      if (existing.utm_source) {
        // First-touch utm already captured; never overwrite.
        return;
      }
      if (!hasUtmNow) {
        // Stored is referrer-only / empty and this touch is also referrer-only:
        // keep the existing one (first referrer wins).
        return;
      }
      // Stored has no utm and a utm is present now; fall through to upgrade.
    }

    const payload = {
      source_label: sourceLabel,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_term: utmTerm,
      referrer_domain: referrer ? referrer.domain : null,
      referrer_url: referrer ? referrer.url : null,
      landing_path: window.location.pathname,
      first_seen_at: new Date().toISOString(),
    };

    storeAcquisition(payload);
    logger.info("Acquisition source captured:", sourceLabel);
  } catch (e) {
    // Tracking must never break app boot.
    logger.warn("Failed to capture acquisition from URL:", e);
  }
}
